/**
 * The Low quality renderer of the 3D floor-plan mesh, and the overlay pass
 * both qualities share.
 *
 * One static interleaved vertex buffer; per frame it rebuilds index lists for
 * the visible surfaces and draws them straight to the screen: solid objects
 * and the cutaway walls whose inward normal faces the camera (back-face
 * culled), dark outlines, and translucent glass. Fills shade by the scene
 * light (sceneLighting.ts) exactly as High shades them, less its ambient
 * occlusion; outlines and glass take their own colors, as in High. Plain
 * WebGL1, so it draws on any context.
 */

import { PALETTE } from '../editor-model';
import { type Mat4 } from '../math3d';
import { type MeshBuilder, type Surface, surfaceShows } from './mesh3d';
import { applySceneLight, SCENE_LIGHT_GLSL } from './sceneLighting';

/** The normal turns into view space as a direction (w = 0), since GLSL ES 1.00 cannot take the 3x3 part of a matrix. */
const VERT = `
attribute vec3 aPos;
attribute vec3 aNormal;
attribute vec3 aColor;
uniform mat4 uViewProj;
uniform mat4 uView;
varying vec3 vViewNormal;
varying vec3 vColor;
void main() {
  vViewNormal = (uView * vec4(aNormal, 0.0)).xyz;
  vColor = aColor;
  gl_Position = uViewProj * vec4(aPos, 1.0);
}`;

/**
 * uShade 1: a fill, shaded by the scene light, two-sided (a back face shades
 * by its flipped normal). uShade 0: an outline, glass or the overlay, in its
 * own color.
 */
const FRAG = `
precision mediump float;
varying vec3 vViewNormal;
varying vec3 vColor;
uniform float uShade;
uniform float uAlpha;
${SCENE_LIGHT_GLSL}
void main() {
  if (uShade < 0.5) { gl_FragColor = vec4(vColor, uAlpha); return; }
  vec3 n = normalize(vViewNormal);
  if (!gl_FrontFacing) n = -n;
  gl_FragColor = vec4(vColor * sceneLight(n, 1.0), uAlpha);
}`;

/**
 * The context attributes every platform shell should request. Opaque: the
 * view draws its own drafting paper and grid behind the scene (see
 * gridRenderer.ts), so nothing behind the surface shows through.
 */
export const GL_CONTEXT_ATTRIBUTES = { antialias: true, alpha: false } as const;

/** A GLSL ES 1.00 program compiled and linked on `gl`, throwing with the driver's log when either step fails. */
export function linkProgram(gl: WebGLRenderingContext, vertexSource: string, fragmentSource: string): WebGLProgram {
  const compile = (type: number, source: string): WebGLShader => {
    const shader = gl.createShader(type)!;
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) throw new Error('shader: ' + gl.getShaderInfoLog(shader));
    return shader;
  };
  const program = gl.createProgram()!;
  gl.attachShader(program, compile(gl.VERTEX_SHADER, vertexSource));
  gl.attachShader(program, compile(gl.FRAGMENT_SHADER, fragmentSource));
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error('link: ' + gl.getProgramInfoLog(program));
  return program;
}

/** WebGL2 detection that works for browser and expo-gl contexts alike. */
const isWebGL2Context = (gl: WebGLRenderingContext): boolean => typeof (gl as WebGL2RenderingContext).texImage3D === 'function';

export class GLRenderer {
  private gl: WebGLRenderingContext;

  /** Release the GL context (View3D.dispose). Safe to call once at teardown. */
  releaseContext(): void {
    this.gl.getExtension('WEBGL_lose_context')?.loseContext();
  }

  private prog: WebGLProgram;
  private uViewProj: WebGLUniformLocation;
  private uView: WebGLUniformLocation;
  private uShade: WebGLUniformLocation;
  private uAlpha: WebGLUniformLocation;
  private aPos: number;
  private aNormal: number;
  private aColor: number;
  private vbo: WebGLBuffer;
  private ibo: WebGLBuffer;
  private uintIndex: boolean;
  private isWebGL2 = false;
  private surfaces: Surface[] = [];
  /** The move gadget's own vertex buffer: rebuilt every frame, drawn over everything. */
  private overlayVbo: WebGLBuffer | null = null;

  /**
   * Takes an already-created context (a browser canvas's or expo-gl's),
   * requested with GL_CONTEXT_ATTRIBUTES. Prefer WebGL2 (High needs MRT and
   * float buffers); Low is plain WebGL1 and runs unchanged on a 2.0 context.
   */
  constructor(gl: WebGLRenderingContext) {
    this.gl = gl;
    this.isWebGL2 = isWebGL2Context(gl);
    this.uintIndex = this.isWebGL2 || !!gl.getExtension('OES_element_index_uint');
    this.prog = linkProgram(gl, VERT, FRAG);
    gl.useProgram(this.prog);
    this.uViewProj = gl.getUniformLocation(this.prog, 'uViewProj')!;
    this.uView = gl.getUniformLocation(this.prog, 'uView')!;
    this.uShade = gl.getUniformLocation(this.prog, 'uShade')!;
    this.uAlpha = gl.getUniformLocation(this.prog, 'uAlpha')!;
    this.aPos = gl.getAttribLocation(this.prog, 'aPos');
    this.aNormal = gl.getAttribLocation(this.prog, 'aNormal');
    this.aColor = gl.getAttribLocation(this.prog, 'aColor');
    this.vbo = gl.createBuffer()!;
    this.ibo = gl.createBuffer()!;
    applySceneLight(gl, this.prog);
  }

  setMesh(mb: MeshBuilder): void {
    const gl = this.gl;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(mb.verts), gl.STATIC_DRAW);
    this.surfaces = mb.surfaces;
  }

  /**
   * Shared resources for High (see litRenderer). Returns null unless we're on
   * a WebGL2 context. High reuses this context and vertex buffer so both
   * qualities draw the identical mesh with no reupload.
   */
  litShare(): { gl: WebGL2RenderingContext; vbo: WebGLBuffer; uintIndex: boolean; getSurfaces: () => Surface[] } | null {
    if (!this.isWebGL2) return null;
    return {
      gl: this.gl as WebGL2RenderingContext,
      vbo: this.vbo,
      uintIndex: this.uintIndex,
      getSurfaces: () => this.surfaces,
    };
  }

  private indexArray(idx: number[]): ArrayBufferView {
    return this.uintIndex ? new Uint32Array(idx) : new Uint16Array(idx);
  }

  private draw(idx: number[]): void {
    if (idx.length === 0) return;
    const gl = this.gl;
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.ibo);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, this.indexArray(idx), gl.DYNAMIC_DRAW);
    const type = this.uintIndex ? gl.UNSIGNED_INT : gl.UNSIGNED_SHORT;
    gl.drawElements(gl.TRIANGLES, idx.length, type, 0);
  }

  private drawLines(idx: number[]): void {
    if (idx.length === 0) return;
    const gl = this.gl;
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.ibo);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, this.indexArray(idx), gl.DYNAMIC_DRAW);
    const type = this.uintIndex ? gl.UNSIGNED_INT : gl.UNSIGNED_SHORT;
    gl.drawElements(gl.LINES, idx.length, type, 0);
  }

  /**
   * Draw a Low frame: clear the screen to the stage's paper, draw the
   * background over it (`drawBackground`, the drafting grid), then the scene
   * in front. `visibleGroups[i]`: is primary wall i facing
   * the camera? Each wall group shows or hides as a unit; ALWAYS surfaces (the
   * floor, solids) draw every frame. Every opaque surface is a real solid
   * (walls have thickness), so one back-face-culled pass draws them all; the
   * dark outlines go on top, then the translucent glass.
   */
  render(viewProj: Mat4, view: Mat4, visibleGroups: boolean[], pxW: number, pxH: number, drawBackground: () => void): void {
    const gl = this.gl;
    gl.viewport(0, 0, pxW, pxH);
    gl.clearColor(...PALETTE.stage, 1);
    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.LEQUAL);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    drawBackground();

    gl.useProgram(this.prog);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    const stride = 9 * 4;
    gl.enableVertexAttribArray(this.aPos);
    gl.vertexAttribPointer(this.aPos, 3, gl.FLOAT, false, stride, 0);
    gl.enableVertexAttribArray(this.aNormal);
    gl.vertexAttribPointer(this.aNormal, 3, gl.FLOAT, false, stride, 12);
    gl.enableVertexAttribArray(this.aColor);
    gl.vertexAttribPointer(this.aColor, 3, gl.FLOAT, false, stride, 24);
    gl.uniformMatrix4fv(this.uViewProj, false, viewProj);
    gl.uniformMatrix4fv(this.uView, false, view);

    const solidFills: number[] = [], wallFills: number[] = [], lines: number[] = [], glass: number[] = [];
    for (const s of this.surfaces) {
      if (!surfaceShows(s, visibleGroups)) continue;
      if (s.group >= 0) push(wallFills, s.fillTris);
      else push(solidFills, s.fillTris);
      push(lines, s.lines);
      push(glass, s.glassTris);
    }

    gl.uniform1f(this.uAlpha, 1);
    gl.uniform1f(this.uShade, 1);
    gl.enable(gl.POLYGON_OFFSET_FILL);
    gl.polygonOffset(1.2, 1.5);
    gl.enable(gl.CULL_FACE);
    gl.cullFace(gl.BACK);
    gl.frontFace(gl.CCW);
    this.draw(solidFills);
    this.draw(wallFills);
    gl.disable(gl.POLYGON_OFFSET_FILL);

    gl.uniform1f(this.uShade, 0);
    this.drawLines(lines);
    if (glass.length) {
      gl.uniform1f(this.uAlpha, 0.4);
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
      gl.depthMask(false);
      this.draw(glass);
      gl.depthMask(true);
      gl.disable(gl.BLEND);
    }
  }

  /**
   * Draw a small mesh over everything already drawn (the move gadget): the
   * depth buffer is cleared first, so the mesh depth-tests against itself but
   * never against the scene; no culling; its colors as given. Leaves the
   * program and the frame's viewport as they are; `render` rebinds the scene
   * next frame.
   */
  renderOverlay(mb: MeshBuilder, viewProj: Mat4): void {
    const gl = this.gl;
    if (!this.overlayVbo) this.overlayVbo = gl.createBuffer()!;
    gl.useProgram(this.prog);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.overlayVbo);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(mb.verts), gl.DYNAMIC_DRAW);
    const stride = 9 * 4;
    gl.enableVertexAttribArray(this.aPos);
    gl.vertexAttribPointer(this.aPos, 3, gl.FLOAT, false, stride, 0);
    gl.enableVertexAttribArray(this.aNormal);
    gl.vertexAttribPointer(this.aNormal, 3, gl.FLOAT, false, stride, 12);
    gl.enableVertexAttribArray(this.aColor);
    gl.vertexAttribPointer(this.aColor, 3, gl.FLOAT, false, stride, 24);
    gl.uniformMatrix4fv(this.uViewProj, false, viewProj);
    gl.uniform1f(this.uAlpha, 1);
    gl.uniform1f(this.uShade, 0);
    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.LEQUAL);
    gl.depthMask(true);
    gl.clear(gl.DEPTH_BUFFER_BIT);
    gl.disable(gl.CULL_FACE);
    gl.disable(gl.BLEND);
    const fills: number[] = [], lines: number[] = [];
    for (const s of mb.surfaces) {
      push(fills, s.fillTris);
      push(lines, s.lines);
    }
    // Fills sit back a hair so the outlines on their faces win the depth test.
    gl.enable(gl.POLYGON_OFFSET_FILL);
    gl.polygonOffset(1.2, 1.5);
    this.draw(fills);
    gl.disable(gl.POLYGON_OFFSET_FILL);
    this.drawLines(lines);
    gl.enable(gl.CULL_FACE);
  }
}

function push(dst: number[], src: number[]): void {
  for (let i = 0; i < src.length; i++) dst.push(src[i]);
}
